/**
 * EmailTemplateEditor — Full-screen email template edit/create page
 *
 * Layout pattern matches certificate-template builder: `fixed inset-0 z-[60]`
 * covers the dashboard chrome so the editor fills the entire viewport.
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ Sticky header: [← Back] [Name] [Type ▼] [⚙][👁][💾 Save]  │
 *   ├──────────┬────────────────────────────────────────────────┤
 *   │ Variables│ ┌─ Subject: [_____________________________]   │
 *   │ panel    │ ├────────────────────────────────────────────┐│
 *   │  • drag  │ │                                            ││
 *   │    to    │ │  GrapesEmailEditor (default GrapesJS UI)   ││
 *   │   canvas │ │                                            ││
 *   │  • click │ └────────────────────────────────────────────┘│
 *   │    to    │                                                │
 *   │   insert │                                                │
 *   │  in subj │                                                │
 *   └──────────┴────────────────────────────────────────────────┘
 *
 * Variables fetched per template_type via getTemplatesByType() and passed
 * to both VariablesPanel (drag to canvas via editor.BlockManager.startDrag,
 * click to insert at subject cursor) and GrapesEmailEditor (registered as
 * blocks so the GrapesJS internal blocks panel also has them).
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import type { Editor, ProjectData } from 'grapesjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Eye,
  AlertCircle,
  Loader2,
  X,
  Mail,
} from 'lucide-react';
import toast from 'react-hot-toast';
import emailTemplateService from '@/services/email-template.service';
import {
  CreateEmailTemplateDto,
  UpdateEmailTemplateDto,
  TemplateVariable,
} from '@/types/email-template.types';
import GrapesEmailEditor from '@/components/email-templates/GrapesEmailEditor';
import CollapsiblePreview from '@/components/email-templates/CollapsiblePreview';
import { extractVariables, replaceVariables, mergeAllowedVariables, editorVariableName } from '@/utils/react-email-converter';
import { buildSectionSamples, placeSectionVariables } from '@/utils/email-section-placement';
import { cleanEditorArtifacts } from '@/utils/email-editor-cleanup';

export default function EmailTemplateEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  // Fix: swap overflow-x-hidden → clip so sticky works on this page only
  useEffect(() => {
    const main = document.querySelector('main');
    if (!main) return;
    main.style.overflowX = 'clip';
    return () => { main.style.overflowX = ''; };
  }, []);

  // Form state — empty template_type in edit mode (loaded from API)
  const [formData, setFormData] = useState<CreateEmailTemplateDto>({
    template_key: '',
    template_name: '',
    template_type: isEditMode ? '' : 'WELCOME_EMAIL',
    subject_template: '',
    html_template: '',
    is_active: true,
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showPreviewOverlay, setShowPreviewOverlay] = useState(false);
  const [editorJsonData, setEditorJsonData] = useState<ProjectData | null>(null);

  // Editor instance handle — captured via GrapesEmailEditor's onEditorReady.
  // Lets handleSave pull the freshly-compiled HTML synchronously, bypassing
  // the React setState latency of the regular onChange → formData flow.
  const editorRef = useRef<Editor | null>(null);

  // ============== QUERIES ==============

  const { data: existingTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['emailTemplate', id],
    queryFn: () => emailTemplateService.getTemplateById(id!),
    enabled: isEditMode,
    // Prevent background refetches that would change `existingTemplate`'s
    // reference while the admin is editing — the sync effect below would then
    // overwrite in-progress form changes with the stale server copy.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: typeTemplates } = useQuery({
    queryKey: ['emailTemplateVariables', formData.template_type],
    queryFn: () => emailTemplateService.getTemplatesByType(formData.template_type),
    enabled: !!formData.template_type,
    staleTime: 5 * 60 * 1000,
  });

  // Deduplicate variables by name across all templates of this type
  const variables: TemplateVariable[] = useMemo(() => {
    if (!typeTemplates || typeTemplates.length === 0) return [];

    const byName = new Map<string, TemplateVariable>();
    typeTemplates.forEach((template) => {
      const vars = (template.allowed_variables || []) as Array<string | TemplateVariable>;
      vars.forEach((v) => {
        const raw: TemplateVariable =
          typeof v === 'string'
            ? { name: v, type: 'string', description: '', required: false }
            : v;
        // Offer the text version of list variables (badges → badge_names).
        const obj: TemplateVariable = raw.name ? { ...raw, name: editorVariableName(raw.name) } : raw;
        if (obj.name && !byName.has(obj.name)) byName.set(obj.name, obj);
      });
    });
    return Array.from(byName.values());
  }, [typeTemplates]);

  // ============== SYNC EXISTING TEMPLATE TO FORM ==============

  // One-shot initialization guard: seed formData from the server copy ONCE,
  // then let the user's edits own the state.  Without this guard, any React
  // Query refetch (post-mutation invalidation, manual refresh, etc.) would
  // return a new `existingTemplate` reference → this effect would fire →
  // setFormData would wipe out in-progress edits.  Observed: admin's {{hello}}
  // edits were silently replaced by the original DB values right before save,
  // so the payload sent to the backend contained the unchanged template.
  const isFormInitializedRef = useRef(false);

  useEffect(() => {
    if (existingTemplate && !isFormInitializedRef.current) {
      setFormData({
        template_key: existingTemplate.template_key,
        template_name: existingTemplate.template_name,
        template_type: existingTemplate.template_type,
        subject_template: existingTemplate.subject_template,
        html_template: existingTemplate.html_template,
        is_active: existingTemplate.is_active,
      });
      isFormInitializedRef.current = true;
    }
  }, [existingTemplate]);

  // ============== MUTATIONS ==============

  const createMutation = useMutation({
    mutationFn: (data: CreateEmailTemplateDto) => emailTemplateService.createTemplate(data),
    onSuccess: () => {
      toast.success('Email template created successfully!');
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      navigate('/admin/enterprise/email-templates');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create template');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateEmailTemplateDto) => emailTemplateService.updateTemplate(id!, data),
    onSuccess: () => {
      toast.success('Email template updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplate', id] });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update template');
    },
  });

  // ============== VALIDATION ==============

  // html_template: pass the live editor HTML when the saved copy may lag behind it.
  const validateForm = (html_template: string = formData.html_template): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.template_key.trim()) {
      errors.template_key = 'Template key is required';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.template_key)) {
      errors.template_key = 'Template key must be letters, numbers, and underscores only';
    }
    if (!formData.template_name.trim()) errors.template_name = 'Template name is required';
    if (!formData.template_type) errors.template_type = 'Template type is required';
    if (!formData.subject_template.trim()) errors.subject_template = 'Subject is required';
    if (!html_template.trim()) errors.html_template = 'Email content is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============== HANDLERS ==============

  const handleInputChange = <K extends keyof CreateEmailTemplateDto>(
    field: K,
    value: CreateEmailTemplateDto[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSave = async () => {
    // New template still being typed: the saved copy can be empty, so check the live editor.
    if (!validateForm(formData.html_template.trim() ? formData.html_template : getLatestHtml())) {
      toast.error('Please fix validation errors');
      return;
    }

    try {
      // Force a synchronous compile against the live editor state — the
      // regular onChange → setFormData path is asynchronous, so if the admin
      // clicks Update while RTE is still focused inside a text block, the
      // blur→rte:disable→onChange sequence queues a setFormData that React
      // hasn't applied by the time handleSave reads `formData.html_template`.
      //
      // CRITICAL safety gate: only accept the compile output if the <body>
      // actually has content.  MJML wrappers are ~3 KB even when the canvas
      // is in a transient state with an empty body — saving that would wipe
      // the admin's real template on the backend.  If the gate rejects, we
      // fall back to formData.html_template (last-known-good, protected by
      // hasRealEmailBody inside GrapesEmailEditor's extractAndNotify).
      let htmlToSave = formData.html_template;
      let jsonToSave: ProjectData | null = editorJsonData;
      const editor = editorRef.current;
      if (editor) {
        try {
          const result = editor.runCommand('mjml-compile-safe') as { html?: string } | undefined;
          const freshHtml = result?.html;
          if (freshHtml) {
            const bodyMatch = freshHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
            const body = bodyMatch ? bodyMatch[1] : freshHtml;
            const text = body.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
            const substantive =
              text.length > 20 || /\{\{|<img\s|<a\s[^>]*href/i.test(body);
            if (substantive) {
              htmlToSave = freshHtml;
            } else {
              console.warn(
                '[EmailTemplateEditor] Pre-save compile produced empty body — keeping formData.html_template',
                { compile_length: freshHtml.length },
              );
            }
          }
          jsonToSave = editor.getProjectData();
        } catch (err) {
          console.warn('[EmailTemplateEditor] Pre-save compile failed, falling back to formData:', err);
        }
      }

      // Never save editor-only marks (copied canvas grey, draggable, frozen list line-height).
      htmlToSave = cleanEditorArtifacts(htmlToSave || '');

      const subjectVars = extractVariables(formData.subject_template || '');
      const htmlVars = extractVariables(htmlToSave || '');
      const allVariables = [...new Set([...subjectVars, ...htmlVars])];
      // Keep already-allowed variables (e.g. start_url) even if not used right now.
      const allowedVariables = mergeAllowedVariables(
        isEditMode ? (existingTemplate?.allowed_variables as Array<string | TemplateVariable> | undefined) : undefined,
        allVariables
      );

      // Diagnostic log — verify what's actually being sent to DB.
      // If html_template is suspiciously short or missing body content, the
      // save is corrupted before it even leaves the browser.
      console.log('[EmailTemplateEditor] Saving template:', {
        template_key: formData.template_key,
        html_length: htmlToSave?.length ?? 0,
        has_body: /<body[^>]*>[\s\S]*<\/body>/i.test(htmlToSave || ''),
        has_variables_detected: allVariables.length,
        editor_json_keys: editorJsonData ? Object.keys(editorJsonData).length : 0,
        html_preview: (htmlToSave || '').substring(0, 300),
      });

      if (isEditMode) {
        updateMutation.mutate({
          template_name: formData.template_name,
          template_type: formData.template_type,
          subject_template: formData.subject_template,
          html_template: htmlToSave,
          editor_json: jsonToSave,
          allowed_variables: allowedVariables,
          is_active: formData.is_active,
        });
      } else {
        createMutation.mutate({
          ...formData,
          html_template: htmlToSave,
          editor_json: jsonToSave,
          allowed_variables: allowedVariables,
        });
      }
    } catch {
      toast.error('Error preparing template data. Please check your template.');
    }
  };

  const handleCancel = () => navigate('/admin/enterprise/email-templates');

  // ============== PREVIEW HELPERS ==============

  // html_source: the HTML being previewed (fresher than formData while text is being edited).
  const generateSampleData = (html_source: string = formData.html_template || ''): Record<string, string> => {
    const subjectVars = extractVariables(formData.subject_template || '');
    const htmlVars = extractVariables(cleanEditorArtifacts(html_source));
    const allVariables = [...new Set([...subjectVars, ...htmlVars])];
    const sample: Record<string, string> = {};
    allVariables.forEach((name) => { sample[name] = name.replace(/_/g, ' '); });
    return sample;
  };

  /**
   * Pull the freshest HTML available for the preview modal.
   *
   * `formData.html_template` is updated via the editor's debounced onChange,
   * so it can lag the live canvas by up to the debounce window (500ms) — or
   * longer if the user is still inside an RTE session (extract is gated on
   * rte:disable). Clicking Preview during that window used to show the stale
   * pre-edit HTML.
   *
   * Fix: force a synchronous compile against the live editor state. Same
   * `substantive` gate as handleSave() — if the compile returns an empty body
   * (transient MJML rerender window), fall back to the debounced formData so
   * we never show a blank preview.
   */
  const getLatestHtml = (): string => {
    const editor = editorRef.current;
    if (!editor) return formData.html_template || '';
    try {
      const result = editor.runCommand('mjml-compile-safe') as { html?: string } | undefined;
      const fresh = result?.html;
      if (!fresh) return formData.html_template || '';
      const bodyMatch = fresh.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      const body = bodyMatch ? bodyMatch[1] : fresh;
      const text = body.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      const substantive = text.length > 20 || /\{\{|<img\s|<a\s[^>]*href/i.test(body);
      return substantive ? fresh : formData.html_template || '';
    } catch {
      return formData.html_template || '';
    }
  };

  const getPreviewHtml = (): string => {
    try {
      const source = cleanEditorArtifacts(getLatestHtml());
      if (!source) {
        return '<p style="color: #999; font-style: italic; padding: 20px;">Enter email content...</p>';
      }
      // Section variables get a sample box, placed the same way the backend does.
      const sample_data = generateSampleData(source);
      const section_samples = buildSectionSamples(Object.keys(sample_data));
      let html = placeSectionVariables(source, section_samples);
      html = replaceVariables(html, sample_data);
      html = html.replace(/\{\{([^}]+)\}\}/g, (m) =>
        `<span style="background-color: yellow; padding: 2px 4px;">${m}</span>`
      );
      html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
      html = html.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
      html = html.replace(/javascript\s*:/gi, '');
      return html;
    } catch {
      return '<p style="color: red; padding: 20px;">Error generating preview.</p>';
    }
  };

  const getPreviewSubject = (): string => {
    try {
      if (!formData.subject_template) return '';
      let subject = replaceVariables(formData.subject_template, generateSampleData());
      subject = subject.replace(/\{\{([^}]+)\}\}/g, (m) => `[${m}]`);
      return subject;
    } catch {
      return 'Error in subject template';
    }
  };

  // ============== EARLY LOADING STATE ==============

  // Two conditions must both be satisfied before rendering the editor:
  //   1. Template fetch is complete (isLoadingTemplate === false)
  //   2. formData has been synced with existingTemplate (useEffect ran)
  // Without condition #2, there's a brief render window where GrapesEmailEditor
  // mounts with empty formData.html_template.  Its initialValueRef captures ""
  // and the editor loads an empty canvas.  A save then overwrites the real DB
  // template with empty compiled MJML.  See git history for the fix payload.
  if (isEditMode && (isLoadingTemplate || !formData.html_template)) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ============== RENDER ==============

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-gray-100">
      {/* ── Sticky header — matches certificate editor style ── */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0 px-6 py-3">
        {/* Row 1: Title + Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <button
              type="button"
              onClick={handleCancel}
              className="h-9 w-9 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Back to templates"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Mail className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span className="truncate">{isEditMode ? 'Edit Email Template' : 'Create Email Template'}</span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-normal flex-shrink-0">
                  MJML Editor
                </span>
              </h1>
              <p className="text-sm text-gray-500 truncate">
                {formData.template_name || 'Untitled Template'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowPreviewOverlay(true)}
              className="h-9 flex items-center gap-1.5 px-3 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              title="Preview email"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="h-9 flex items-center gap-1.5 px-3 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="h-9 flex items-center gap-1.5 px-4 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update' : 'Create'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Row 2: Subject — aligned under title text */}
        <div className="flex items-center gap-2 mt-2 pl-[52px]">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider flex-shrink-0">
            Subject:
          </span>
          <input
            type="text"
            value={formData.subject_template}
            onChange={(e) => handleInputChange('subject_template', e.target.value)}
            placeholder="e.g., Welcome to SkillZen, {{user_name}}!"
            className={`flex-1 max-w-3xl h-7 px-2.5 text-sm font-mono bg-gray-50 border rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-colors ${
              validationErrors.subject_template ? 'border-red-500 bg-red-50' : 'border-gray-200'
            }`}
          />
          {validationErrors.subject_template && (
            <span className="text-xs text-red-500 flex items-center gap-1 flex-shrink-0">
              <AlertCircle className="w-3 h-3" />
              Required
            </span>
          )}
        </div>
      </header>

      {/* ── Main: GrapesJS editor fills all remaining space ── */}
      <main className="flex-1 flex overflow-hidden">
        <section className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Editor area — fills all remaining vertical space.
              GrapesEmailEditor handles HTML→MJML conversion internally on load. */}
          <div className="flex-1 overflow-hidden">
            <GrapesEmailEditor
              label=""
              value={formData.html_template}
              onChange={(html) => handleInputChange('html_template', html)}
              editorJson={editorJsonData || existingTemplate?.editor_json}
              onJsonChange={setEditorJsonData}
              onEditorReady={(editor) => { editorRef.current = editor; }}
              variables={variables}
              error={validationErrors.html_template}
              required
              minHeight="100%"
            />
          </div>
        </section>
      </main>

      {/* ── Preview modal ── */}
      {showPreviewOverlay && (
        <div
          className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4 overflow-hidden"
          ref={(node) => {
            if (node) document.body.style.overflow = 'hidden';
            else document.body.style.overflow = '';
          }}
        >
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-500" />
                <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPreviewOverlay(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close preview"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <CollapsiblePreview
                subjectPreview={getPreviewSubject()}
                htmlPreview={getPreviewHtml()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 